'use client';

import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/lib/auth-context';

/**
 * Global keyboard shortcuts.
 *
 *   /       focus the navbar search
 *   n       new prompt (auth required)
 *   g h     go home
 *   g p     go to "my prompts"
 *   g s     go to "saved"
 *   g t     go to "tags"
 *   ?       toggle shortcut help
 *   Esc     close help / dropdowns (pages handle their own)
 *
 * Shortcuts are inert while the user is typing in an input/textarea/editable.
 */
export default function KeyboardShortcuts() {
  const router = useRouter();
  const { user } = useAuth();
  const [helpOpen, setHelpOpen] = useState(false);
  const pendingG = useRef<number | null>(null);

  useEffect(() => {
    const clearPendingG = () => {
      if (pendingG.current) {
        window.clearTimeout(pendingG.current);
        pendingG.current = null;
      }
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        clearPendingG();
        setHelpOpen(false);
        return;
      }

      // Cmd+K / Ctrl+K → open the global command palette (Spotlight-style).
      // Works even while typing in inputs — intentional, so users can pop
      // it open from anywhere.
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('cmdpalette:open'));
        return;
      }

      const target = e.target as HTMLElement | null;
      const isTyping =
        !!target &&
        (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.isContentEditable);
      if (isTyping) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const key = e.key.toLowerCase();

      // Two-key sequence: `g` then <key>
      if (pendingG.current) {
        clearPendingG();
        if (key === 'h') { e.preventDefault(); router.push('/'); return; }
        if (key === 'p') { e.preventDefault(); router.push(user ? '/my/prompts' : '/auth/login'); return; }
        if (key === 's') { e.preventDefault(); router.push(user ? '/my/saved' : '/auth/login'); return; }
        if (key === 't') { e.preventDefault(); router.push('/tags'); return; }
        if (key === 'r') { e.preventDefault(); router.push('/trending'); return; }
      }

      if (key === '/') {
        e.preventDefault();
        const el = document.getElementById('global-search') as HTMLInputElement | null;
        if (el) { el.focus(); el.select(); }
      } else if (key === 'n') {
        e.preventDefault();
        router.push(user ? '/create' : '/auth/login');
      } else if (key === '?') {
        e.preventDefault();
        setHelpOpen((o) => !o);
      } else if (key === 'g') {
        clearPendingG();
        pendingG.current = window.setTimeout(clearPendingG, 700);
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [router, user]);

  if (!helpOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[90] px-4"
      onClick={() => setHelpOpen(false)}
    >
      <div
        className="bg-surface-container-lowest rounded-2xl p-8 max-w-md w-full shadow-2xl border border-outline-variant"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <span className="font-label text-[10px] uppercase tracking-[0.2em] text-indigo-600 font-bold block mb-1">
              Shortcuts
            </span>
            <h2 className="font-headline text-2xl text-on-surface">Keyboard reference</h2>
          </div>
          <button
            onClick={() => setHelpOpen(false)}
            className="w-8 h-8 rounded-full text-on-surface-variant hover:bg-surface-container-low flex items-center justify-center"
            aria-label="Close"
          >
            <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>close</span>
          </button>
        </div>
        <dl className="space-y-3 text-sm">
          <Row keys={['/']} description="Focus the search" />
          <Row keys={['⌘', 'K']} description="Focus the search (also)" />
          <Row keys={['n']} description="New prompt" />
          <Row keys={['g', 'h']} description="Go to gallery" />
          <Row keys={['g', 'r']} description="Go to trending" />
          <Row keys={['g', 'p']} description="Go to my prompts" />
          <Row keys={['g', 's']} description="Go to saved" />
          <Row keys={['g', 't']} description="Go to all tags" />
          <Row keys={['?']} description="This help" />
          <Row keys={['Esc']} description="Close dialogs" />
        </dl>
        <p className="mt-6 text-xs text-outline">
          Shortcuts pause while you&rsquo;re typing in a field.
        </p>
      </div>
    </div>
  );
}

function Row({ keys, description }: { keys: string[]; description: string }) {
  return (
    <div className="flex items-center justify-between gap-4 py-1">
      <span className="text-on-surface-variant">{description}</span>
      <span className="flex items-center gap-1">
        {keys.map((k, i) => (
          <span key={i} className="flex items-center gap-1">
            {i > 0 && <span className="text-outline text-[10px]">then</span>}
            <kbd className="px-2 py-0.5 bg-surface-container-low border border-outline-variant rounded text-[11px] font-mono font-semibold text-on-surface min-w-[24px] text-center">
              {k}
            </kbd>
          </span>
        ))}
      </span>
    </div>
  );
}
